import {
  MeetingType,
  CreateOtherMeetingInviteMutation,
  CreateOtherMeetingInviteMutationVariables,
  MeetingStepViewId,
  InviteToAppraisalMeetingMutationVariables,
  InviteToAppraisalMeetingMutation,
  SkipMeetingStepReason,
  MeetingInviteInput,
  Country,
} from '../../../types'
import { CREATE_OTHER_MEETING_INVITE, INVITE_APPRAISAL, MEETINGS_BY_MANAGER, MEETINGS_BY_REPLYER } from '../meeting.graphql'
import { client } from '../../..'
import { SUBORDINATES_BY_EMPLOYEE, EMPLOYEES_AND_APPRAISALS, BU_EMPLOYEES_AND_APPRAISALS } from '../../employee/Queries'
import { INVITE_OPTIONS } from '../../invite-options/inviteOptions'
import { GraphQLFormattedError } from 'graphql'
import { COMPANY_MANAGER_DASHBOARD_APPRAISAL_MEETING_STATUS } from '../../company-manager-dashboard/appraisal-meeting-status/Queries'

export interface MeetingInviteProps {
  employeeId: number
  appraisalSeasonId: number
  reviewerId: number
  meetingDate: string
  meetingType: MeetingType
  viewIds?: MeetingStepViewId[]
  meetingId?: number
  meetingAgenda?: string
  emailText?: string
  location?: string
  reasonForReducedSteps?: SkipMeetingStepReason
  duration?: number | null
  additionalParticipantsIds: number[]
}

export default (): {
  createMeetingInvite: (inviteAction: MeetingInviteProps) => Promise<{
    data: InviteToAppraisalMeetingMutation | CreateOtherMeetingInviteMutation | null | undefined
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    errors: readonly GraphQLFormattedError<Record<string, any>>[] | undefined
  }>
  createAppraisalInvite: (
    inviteAction: MeetingInviteProps,
    companyId: string,
    country: Country,
    businessUnitId?: string,
  ) => Promise<{
    data: InviteToAppraisalMeetingMutation | null | undefined
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    errors: readonly GraphQLFormattedError<Record<string, any>>[] | undefined
  }>
  createDashboardAppraisalInvite: (
    inviteAction: MeetingInviteProps,
    companyId: string,
  ) => Promise<{
    data: InviteToAppraisalMeetingMutation | null | undefined
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    errors: readonly GraphQLFormattedError<Record<string, any>>[] | undefined
  }>
  createAppraisalInvitePublicProfile: (inviteAction: MeetingInviteProps) => Promise<{
    data: InviteToAppraisalMeetingMutation | null | undefined
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    errors: readonly GraphQLFormattedError<Record<string, any>>[] | undefined
  }>
} => {
  const createMeetingInvite = async (inviteAction: MeetingInviteProps) => {
    if ([MeetingType.Appraisal, MeetingType.AppraisalUpdate].includes(inviteAction.meetingType)) {
      const meetingInput: MeetingInviteInput = {
        appraisalSeasonId: inviteAction.appraisalSeasonId,
        employeeId: inviteAction.employeeId,
        reviewerId: inviteAction.reviewerId,
        emailText: inviteAction.emailText ? inviteAction.emailText : '',
        meetingDate: inviteAction.meetingDate,
        meetingType: inviteAction.meetingType,
        viewIds: inviteAction.viewIds ? inviteAction.viewIds : [],
        location: inviteAction.location,
        reasonForReducedSteps: inviteAction.reasonForReducedSteps,
        additionalParticipantsIds: inviteAction.additionalParticipantsIds,
        duration: inviteAction.duration,
      }
      const { data, errors } = await client.mutate<InviteToAppraisalMeetingMutation, InviteToAppraisalMeetingMutationVariables>({
        mutation: INVITE_APPRAISAL,
        variables: {
          meetingInput,
        },
        refetchQueries: [
          {
            query: MEETINGS_BY_MANAGER,
            variables: { managerId: inviteAction.reviewerId },
          },
          {
            query: MEETINGS_BY_REPLYER,
            variables: { replyerId: inviteAction.employeeId },
          },
          {
            query: SUBORDINATES_BY_EMPLOYEE,
            variables: { employeeId: inviteAction.reviewerId },
          },
          {
            query: INVITE_OPTIONS,
            variables: { employeeId: inviteAction.employeeId, appraisalSeasonId: inviteAction.appraisalSeasonId },
          },
        ],
        awaitRefetchQueries: true,
      })
      return { data, errors }
    } else {
      const meetingInput: MeetingInviteInput = {
        appraisalSeasonId: inviteAction.appraisalSeasonId,
        employeeId: inviteAction.employeeId,
        reviewerId: inviteAction.reviewerId,
        meetingAgenda: inviteAction.meetingAgenda ? inviteAction.meetingAgenda : '',
        emailText: inviteAction.emailText ? inviteAction.emailText : '',
        meetingDate: inviteAction.meetingDate,
        meetingType: inviteAction.meetingType,
        location: inviteAction.location,
        additionalParticipantsIds: inviteAction.additionalParticipantsIds,
        duration: inviteAction.duration,
      }
      const { data, errors } = await client.mutate<CreateOtherMeetingInviteMutation, CreateOtherMeetingInviteMutationVariables>({
        mutation: CREATE_OTHER_MEETING_INVITE,
        variables: {
          meetingInput,
        },
        refetchQueries: [
          {
            query: MEETINGS_BY_MANAGER,
            variables: { managerId: inviteAction.reviewerId },
          },
          {
            query: MEETINGS_BY_REPLYER,
            variables: { replyerId: inviteAction.employeeId },
          },
        ],
      })
      return { data, errors }
    }
  }

  const createAppraisalInvite = async (inviteAction: MeetingInviteProps, companyId: string, country: Country, businessUnitId?: string) => {
    const refetchQueries = !!businessUnitId
      ? [
          {
            query: MEETINGS_BY_MANAGER,
            variables: { managerId: inviteAction.reviewerId },
          },
          {
            query: MEETINGS_BY_REPLYER,
            variables: { replyerId: inviteAction.employeeId },
          },
          {
            query: SUBORDINATES_BY_EMPLOYEE,
            variables: { employeeId: inviteAction.reviewerId },
          },
          {
            query: INVITE_OPTIONS,
            variables: { employeeId: inviteAction.employeeId, appraisalSeasonId: inviteAction.appraisalSeasonId },
          },
          {
            query: BU_EMPLOYEES_AND_APPRAISALS,
            variables: { businessUnitId, country },
          },
        ]
      : [
          {
            query: MEETINGS_BY_MANAGER,
            variables: { managerId: inviteAction.reviewerId },
          },
          {
            query: MEETINGS_BY_REPLYER,
            variables: { replyerId: inviteAction.employeeId },
          },
          {
            query: SUBORDINATES_BY_EMPLOYEE,
            variables: { employeeId: inviteAction.reviewerId },
          },
          {
            query: INVITE_OPTIONS,
            variables: { employeeId: inviteAction.employeeId, appraisalSeasonId: inviteAction.appraisalSeasonId },
          },
          {
            query: EMPLOYEES_AND_APPRAISALS,
            variables: { companyId, country },
          },
        ]

    const meetingInput: MeetingInviteInput = {
      appraisalSeasonId: inviteAction.appraisalSeasonId,
      employeeId: inviteAction.employeeId,
      reviewerId: inviteAction.reviewerId,
      emailText: inviteAction.emailText ? inviteAction.emailText : '',
      meetingDate: inviteAction.meetingDate,
      meetingType: inviteAction.meetingType,
      viewIds: inviteAction.viewIds ? inviteAction.viewIds : [],
      location: inviteAction.location,
      reasonForReducedSteps: inviteAction.reasonForReducedSteps,
      additionalParticipantsIds: inviteAction.additionalParticipantsIds,
      duration: inviteAction.duration,
    }
    const { data, errors } = await client.mutate<InviteToAppraisalMeetingMutation, InviteToAppraisalMeetingMutationVariables>({
      mutation: INVITE_APPRAISAL,
      variables: {
        meetingInput,
      },
      refetchQueries,
    })
    return { data, errors }
  }

  const createDashboardAppraisalInvite = async (inviteAction: MeetingInviteProps, companyId: string) => {
    const refetchQueries = [
      {
        query: COMPANY_MANAGER_DASHBOARD_APPRAISAL_MEETING_STATUS,
        variables: {
          companyId,
        },
      },
    ]

    const meetingInput: MeetingInviteInput = {
      appraisalSeasonId: inviteAction.appraisalSeasonId,
      employeeId: inviteAction.employeeId,
      reviewerId: inviteAction.reviewerId,
      emailText: inviteAction.emailText ? inviteAction.emailText : '',
      meetingDate: inviteAction.meetingDate,
      meetingType: inviteAction.meetingType,
      viewIds: inviteAction.viewIds ? inviteAction.viewIds : [],
      location: inviteAction.location,
      reasonForReducedSteps: inviteAction.reasonForReducedSteps,
      additionalParticipantsIds: inviteAction.additionalParticipantsIds,
      duration: inviteAction.duration,
    }
    const { data, errors } = await client.mutate<InviteToAppraisalMeetingMutation, InviteToAppraisalMeetingMutationVariables>({
      mutation: INVITE_APPRAISAL,
      variables: {
        meetingInput,
      },
      refetchQueries,
      awaitRefetchQueries: true,
    })
    return { data, errors }
  }

  const createAppraisalInvitePublicProfile = async (inviteAction: MeetingInviteProps) => {
    const meetingInput: MeetingInviteInput = {
      appraisalSeasonId: inviteAction.appraisalSeasonId,
      employeeId: inviteAction.employeeId,
      reviewerId: inviteAction.reviewerId,
      emailText: inviteAction.emailText ? inviteAction.emailText : '',
      meetingDate: inviteAction.meetingDate,
      meetingType: inviteAction.meetingType,
      viewIds: inviteAction.viewIds ? inviteAction.viewIds : [],
      location: inviteAction.location,
      reasonForReducedSteps: inviteAction.reasonForReducedSteps,
      additionalParticipantsIds: inviteAction.additionalParticipantsIds,
      duration: inviteAction.duration,
    }
    const { data, errors } = await client.mutate<InviteToAppraisalMeetingMutation, InviteToAppraisalMeetingMutationVariables>({
      mutation: INVITE_APPRAISAL,
      variables: {
        meetingInput,
      },
      refetchQueries: [
        {
          query: MEETINGS_BY_REPLYER,
          variables: { replyerId: inviteAction.employeeId, disputedMeetingsIncluded: true },
        },
      ],
      awaitRefetchQueries: true,
    })
    return { data, errors }
  }

  return { createMeetingInvite, createAppraisalInvite, createDashboardAppraisalInvite, createAppraisalInvitePublicProfile }
}
